import { graphql } from "gql.tada";

import { AdminActorContextFragment } from "./actor-contexts/AdminActorContext";
import { ApiClientActorContextFragment } from "./actor-contexts/ApiClientActorContext";
import { MarketplaceModuleActorContextFragment } from "./actor-contexts/MarketplaceModuleActorContext";
import { PlayerActorContextFragment } from "./actor-contexts/PlayerActorContext";
import { SimulationActorContextFragment } from "./actor-contexts/SimulationActorContext";
import { SystemActorContextFragment } from "./actor-contexts/SystemActorContext";
import { WebhookCallbackActorContextFragment } from "./actor-contexts/WebhookCallbackActorContext";
import { RuleEngineAdvancementRuleFiredFragment } from "./payloads/RuleEngineAdvancementRuleFiredPayload";
import { RuleEngineCrossStepTransferredFragment } from "./payloads/RuleEngineCrossStepTransferredPayload";
import { RuleEngineEffectAdvanceFragment } from "./payloads/RuleEngineEffectAdvancePayload";
import { RuleEngineEffectCreateGroupFragment } from "./payloads/RuleEngineEffectCreateGroupPayload";
import { RuleEngineEffectEliminateFragment } from "./payloads/RuleEngineEffectEliminatePayload";
import { RuleEngineEffectEmitSeedingPinFragment } from "./payloads/RuleEngineEffectEmitSeedingPinPayload";
import { RuleEngineEffectEndStepFragment } from "./payloads/RuleEngineEffectEndStepPayload";
import { RuleEngineEffectSetMetadataFragment } from "./payloads/RuleEngineEffectSetMetadataPayload";
import { RuleEngineGenerationScriptRanFragment } from "./payloads/RuleEngineGenerationScriptRanPayload";
import { RuleEngineManualOverrideFragment } from "./payloads/RuleEngineManualOverridePayload";
import { TournamentsStepGeneratedFragment } from "./payloads/TournamentsStepGeneratedPayload";
import { TournamentsStepResetFragment } from "./payloads/TournamentsStepResetPayload";

// Excludes `payload` — fetched lazily by JOURNAL_ENTRY_PAYLOAD_QUERY so the
// 12-member payload union does not compound the list query's static cost
// past the graphql-armor cost-limit budget.
export const JournalEntrySummaryFragment = graphql(
  `
    fragment JournalEntrySummary on JournalEntry {
      id
      category
      type
      severity
      createdAt
      parentEntryId
      correlationId
      primaryResource {
        type
        id
      }
      secondaryResources {
        type
        id
      }
      actor {
        type
        id
        context {
          __typename
          ...AdminActorContextFields
          ...PlayerActorContextFields
          ...SystemActorContextFields
          ...MarketplaceModuleActorContextFields
          ...ApiClientActorContextFields
          ...WebhookCallbackActorContextFields
          ...SimulationActorContextFields
        }
      }
    }
  `,
  [
    AdminActorContextFragment,
    PlayerActorContextFragment,
    SystemActorContextFragment,
    MarketplaceModuleActorContextFragment,
    ApiClientActorContextFragment,
    WebhookCallbackActorContextFragment,
    SimulationActorContextFragment,
  ],
);

export const JournalEntryPayloadFragment = graphql(
  `
    fragment JournalEntryPayload on JournalEntry {
      id
      payload {
        __typename
        ...RuleEngineAdvancementRuleFiredFields
        ...RuleEngineGenerationScriptRanFields
        ...RuleEngineCrossStepTransferredFields
        ...RuleEngineManualOverrideFields
        ...RuleEngineEffectAdvanceFields
        ...RuleEngineEffectEliminateFields
        ...RuleEngineEffectSetMetadataFields
        ...RuleEngineEffectEndStepFields
        ...RuleEngineEffectCreateGroupFields
        ...RuleEngineEffectEmitSeedingPinFields
        ...TournamentsStepResetFields
        ...TournamentsStepGeneratedFields
      }
    }
  `,
  [
    RuleEngineAdvancementRuleFiredFragment,
    RuleEngineGenerationScriptRanFragment,
    RuleEngineCrossStepTransferredFragment,
    RuleEngineManualOverrideFragment,
    RuleEngineEffectAdvanceFragment,
    RuleEngineEffectEliminateFragment,
    RuleEngineEffectSetMetadataFragment,
    RuleEngineEffectEndStepFragment,
    RuleEngineEffectCreateGroupFragment,
    RuleEngineEffectEmitSeedingPinFragment,
    TournamentsStepResetFragment,
    TournamentsStepGeneratedFragment,
  ],
);

export const JOURNAL_ENTRIES_QUERY = graphql(
  `
    query JournalEntries($page: PageInfo, $filters: JournalFiltersInput) {
      journalEntries(page: $page, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...JournalEntrySummary
          }
        }
      }
    }
  `,
  [JournalEntrySummaryFragment],
);

export const JOURNAL_ENTRY_ADDED_SUBSCRIPTION = graphql(
  `
    subscription JournalEntryAdded($filters: JournalFiltersInput) {
      journalEntryAdded(filters: $filters) {
        ...JournalEntrySummary
      }
    }
  `,
  [JournalEntrySummaryFragment],
);

export const JOURNAL_ENTRY_PAYLOAD_QUERY = graphql(
  `
    query JournalEntryPayloadQuery($id: ID!) {
      journalEntry(id: $id) {
        ...JournalEntryPayload
      }
    }
  `,
  [JournalEntryPayloadFragment],
);
